// ABOUTME: Checkout subscription activation view for the webshop credentials sidepanel (relation side).
// ABOUTME: Shown in place of the shipping/checkout config when there is no active checkout subscription.

import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { tokens } from "../../../../../theme";
import { formatPrice } from "../../../../../utils/helpers";
import { useGetSubscriptionPricesQuery, useCreateSubscriptionMutation } from "../../../../../newapi/subscription/subscriptionSlice";
import { toast } from "react-toastify";
import { showToastMessageRequestError } from "../../../../../utils/toasts";
import ButtonGroup from "../../../../theme/buttons/ButtonGroup";
import Shbutton from "../../../../theme/buttons/Shbutton";
import Dropdown from "../../../../theme/dropout/DropOut";
import Text from "../../../../theme/text/Text";
import Subheading from "../../../../theme/text/Subheading";
import SpDesktopFooter from "../../../../theme/sidepanel2/SidepanelFooter";

const GridActions = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            <Shbutton
                isLoading={props.isLoading}
                disabled={props.disabled}
                className={"group-btn"}
                onClick={props.action}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<RocketLaunchIcon />}
            >
                {t('activate_subscription')}
            </Shbutton>
        </ButtonGroup>
    );
};

const CheckoutSubscription = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: prices } = useGetSubscriptionPricesQuery('checkout');
    const monthlyPrice = prices?.monthly;
    const priceConfigured = monthlyPrice !== null && monthlyPrice !== undefined;

    const [createSubscription, { isLoading: isActivating }] = useCreateSubscriptionMutation();

    const handleActivate = useCallback(async () => {
        if (isActivating || !priceConfigured) return;
        try {
            // The create endpoint returns the subscription entity (with an id), not a { success } envelope.
            const response = await createSubscription({
                type: 'checkout',
                externalId: props.webshopCredentialsId,
            }).unwrap();
            if (response?.id) {
                toast.success(t('activate_subscription_successful'), {
                    position: toast.POSITION.TOP_RIGHT
                });
            } else {
                showToastMessageRequestError(t("update_failed"), t, { data: response });
            }
        } catch (err) {
            showToastMessageRequestError(t("update_failed"), t, err);
        }
    }, [isActivating, priceConfigured, createSubscription, props.webshopCredentialsId, t]);

    useEffect(() => {
        if (!props.replaceView) return;
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions
                isLoading={isActivating}
                disabled={!priceConfigured}
                action={handleActivate}
            />,
        });

        return () => {};
    }, [isActivating, priceConfigured, handleActivate]);

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} paddingX={3}
            sx={{
                "@media screen and (min-width: 48.1em)": {
                    marginBottom: 11
                }
            }}
        >
            <Dropdown title={t("checkout_subscription")} open>
                <Box sx={{ paddingTop: 2, paddingBottom: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Text light>{t("checkout_subscription_explainer")}</Text>

                    <Box sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1.5,
                        padding: 2,
                        borderRadius: 4,
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                    }}>
                        {priceConfigured ? (
                            <>
                                <Subheading>{formatPrice(i18n, monthlyPrice)}</Subheading>
                                <Text light>{t("per_month")}</Text>
                            </>
                        ) : (
                            <Text light>{t("subscription_price_not_configured")}</Text>
                        )}
                    </Box>
                </Box>
            </Dropdown>

            <SpDesktopFooter>
                <GridActions
                    isLoading={isActivating}
                    disabled={!priceConfigured}
                    action={handleActivate}
                />
            </SpDesktopFooter>
        </Box>
    );
};

export default CheckoutSubscription;
